/**
 * Apply Flow Types & Constants
 * Extracted from apply-flow.tsx for better organization
 * Created: January 23, 2026
 */

// Flow step type
export type FlowStep = 'auth' | 'capture' | 'questions' | 'job_questions' | 'ai_questions' | 'review';

// Job info for display
export interface JobInfo {
  id: string;
  title: string;
  company: string;
  location: string;
  salary?: string;
  experience?: string;
  skills?: string[];
  tenantId?: string;
}

// Conditional field for questions
export interface ConditionalField {
  showIf: string;
  fieldType: string;
  fieldLabel: string;
  fieldId?: string;
  options?: string[];
}

// Question interface
export interface Question {
  id: string;
  text: string;
  type: string;
  source: 'platform' | 'job' | 'tenant' | 'ai';
  required?: boolean;
  options?: string[];
  conditionalFields?: ConditionalField[];
  conditionalField?: ConditionalField;
  difficulty?: 'easy' | 'medium' | 'hard';
  skillAssessed?: string;
  timeLimit?: number;
  category?: string;
  validation?: unknown;
}

// Parsed resume data
export interface ParsedData {
  fullName?: string;
  email?: string;
  mobile?: string;
  currentRole?: string;
  currentCompany?: string;
  totalExperience?: number;
  skills?: string[];
  location?: string;
}

// Match analysis from AI
export interface MatchAnalysis {
  matchedSkills: string[];
  missingSkills: string[];
  experienceRelevance: string;
}

// Static platform questions — cold-paint fallback while D1 platform_questions loads.
// Order and `category` mirror the live D1 table so first paint matches the API response
// and avoids reflow when the BFF arrives. Mobile is owned by the capture step, not here.
export const PLATFORM_QUESTIONS: Question[] = [
  // === AVAILABILITY ===
  {
    id: 'platform_notice_period',
    text: 'Are you currently serving notice period?',
    type: 'yes_no',
    source: 'platform',
    required: true,
    category: 'availability',
    conditionalFields: [
      { showIf: 'yes', fieldType: 'date', fieldLabel: 'Last Working Day (LWD)', fieldId: 'platform_last_working_day' },
      { showIf: 'no', fieldType: 'dropdown', fieldLabel: 'Notice Period (Days)', fieldId: 'platform_notice_period_days', options: ['Immediate', '15 Days', '30 Days', '60 Days', '90 Days', '90+ Days'] }
    ]
  },
  { id: 'platform_open_to_work', text: 'Are you actively looking for new opportunities?', type: 'dropdown', source: 'platform', required: true, category: 'availability', options: ['Actively looking', 'Open to opportunities', 'Not looking', 'Just exploring'] },
  { id: 'platform_willing_to_relocate', text: 'Are you willing to relocate for this opportunity?', type: 'yes_no', source: 'platform', required: true, category: 'availability' },

  // === COMPENSATION ===
  { id: 'platform_current_ctc', text: 'What is your current CTC (Cost to Company)?', type: 'currency', source: 'platform', required: true, category: 'compensation' },
  { id: 'platform_expected_ctc', text: 'What is your expected CTC?', type: 'currency', source: 'platform', required: true, category: 'compensation' },

  // === LOCATION ===
  { id: 'platform_current_location', text: 'What is your current location?', type: 'places', source: 'platform', required: true, category: 'location' },
  { id: 'platform_preferred_locations', text: 'What are your preferred work locations?', type: 'location_multiselect', source: 'platform', required: true, category: 'location' },

  // === PREFERENCES ===
  { id: 'platform_employment_types', text: 'What type of employment are you looking for?', type: 'checkbox_group', source: 'platform', required: true, category: 'preferences', options: ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'] },
  { id: 'platform_work_mode', text: 'What is your preferred work mode?', type: 'dropdown', source: 'platform', required: true, category: 'preferences', options: ['Full-time Office', 'Hybrid (Office + Remote)', 'Remote Only', 'Any (No preference)'] },

  // === PERSONAL ===
  { id: 'platform_dob', text: 'What is your date of birth?', type: 'date', source: 'platform', required: true, category: 'personal' },
  { id: 'platform_nationality', text: 'What is your nationality?', type: 'dropdown', source: 'platform', required: true, category: 'personal', options: ['Indian', 'NRI', 'OCI', 'Foreign National'] },
  { id: 'platform_marital_status', text: 'What is your marital status?', type: 'dropdown', source: 'platform', required: false, category: 'personal', options: ['Single', 'Married', 'Divorced', 'Widowed', 'Prefer not to say'] },
  { id: 'platform_gender', text: 'What is your gender?', type: 'dropdown', source: 'platform', required: false, category: 'personal', options: ['Male', 'Female', 'Other', 'Prefer not to say'] }
];
